import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Anchor, BookOpen, Target } from 'lucide-react';
import AuthModal from '../components/auth/AuthModal';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';

// Only accept same-origin, path-only `next` values. Prevents open-redirect
// attacks (e.g., ?next=https://evil.com). Absolute paths must start with `/`
// and cannot contain `://` or a protocol scheme.
const sanitizeNextPath = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null; // protocol-relative
  if (/[:\\]/.test(trimmed)) return null;
  return trimmed;
};

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [searchParams] = useSearchParams();
  const nextPath = sanitizeNextPath(searchParams.get('next'));

  // Auto-open login modal when arriving via a deep-link redirect (e.g., from
  // an email → protected page → AuthenticatedRoute → /?next=...).
  useEffect(() => {
    if (nextPath && !isAuthenticated()) {
      setShowAuth(true);
    }
  }, [nextPath, isAuthenticated]);

  // After successful login, navigate to the requested `next` path.
  useEffect(() => {
    if (isAuthenticated() && nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [isAuthenticated, nextPath, navigate]);

  const handleGetStarted = () => {
    navigate('/grade');
  };

  return (
    <div className="min-h-screen wave-background">
      {/* Header */}
      <header className="container mx-auto px-4 py-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Anchor className="w-8 h-8 text-white" />
          <span className="text-2xl font-bold text-white">MARQ</span>
        </div>
        {!isAuthenticated() ? (
          <Button
            data-testid="header-login-btn"
            onClick={() => setShowAuth(true)}
            variant="outline"
            className="bg-white text-[#0A7EA4] hover:bg-gray-100 border-0"
          >
            Login / Register
          </Button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-white text-sm">Welcome, {user?.name}</span>
            <Button
              data-testid="header-profile-btn"
              onClick={() => navigate('/profile')}
              variant="outline"
              className="bg-white text-[#0A7EA4] hover:bg-gray-100 border-0"
            >
              Profile
            </Button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-20 page-transition">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            MARQ by HelmTorq
          </h1>
          <p className="text-lg sm:text-xl text-white/90 mb-12 max-w-2xl mx-auto">
            Your comprehensive question bank for Indian maritime certification exams.
            Navigate your certification journey with confidence.
          </p>
          <Button
            data-testid="get-started-btn"
            onClick={handleGetStarted}
            size="lg"
            className="bg-white text-[#0A7EA4] hover:bg-gray-100 text-lg px-8 py-6 rounded-full font-semibold"
          >
            Get Started
          </Button>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-20 max-w-5xl mx-auto">
          <Card data-testid="feature-card-questions" className="bg-white/95 backdrop-blur-sm border-0 hover-lift">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-[#0A7EA4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-[#0A7EA4]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Comprehensive Questions</h3>
              <p className="text-gray-600 text-sm">
                Access a vast repository of questions curated for MEO and Master certifications
              </p>
            </CardContent>
          </Card>

          <Card data-testid="feature-card-practice" className="bg-white/95 backdrop-blur-sm border-0 hover-lift">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-[#8B5CF6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-[#8B5CF6]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Practice Sessions</h3>
              <p className="text-gray-600 text-sm">
                Test your knowledge with customizable practice sessions filtered by topics and surveyors
              </p>
            </CardContent>
          </Card>

          <Card data-testid="feature-card-progress" className="bg-white/95 backdrop-blur-sm border-0 hover-lift">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-[#FF6B6B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Anchor className="w-8 h-8 text-[#FF6B6B]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Track Progress</h3>
              <p className="text-gray-600 text-sm">
                Monitor your preparation journey with bookmarks and progress tracking
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </div>
  );
};

export default HomePage;